// @ts-nocheck
import React, { useEffect, useMemo, useState, type JSX } from 'react';
import { Box, Checkbox, Divider, Grid, IconButton, List, Stack, Typography } from '@mui/material';
import { Close, DragHandle, Link } from '@mui/icons-material';
import { Ups } from '@brightlayer-ui/icons-mui';
import { DragDropContext, Draggable, Droppable, type DropResult } from 'react-beautiful-dnd';
import { CustomModal } from 'components/CustomModal';
import { InputWithCustomPlaceholder } from 'components/InputWithCustomPlaceholder';
import { CustomTransComponent, GroupPath } from 'components';
import { useDebounce, useLoadGroupsScrollableList, useNewCustomTranslations } from 'hooks';
import { useGetLoadGroupsListAllQuery } from '@fiji/common/src/features/loadGroups/loadGroupsApi';
import { useTranslation } from 'react-i18next';
import { ListSkeleton } from 'pages/WidgetManagement/common/Skeletons';
import { InfoListItem } from '@brightlayer-ui/react-components';

interface SelectLoadGroupsModalProps {
    isVisible: boolean;
    onCancel: () => void;
    onConfirm: (selectedGroups: any[]) => void;
    isMultiDevice: boolean;
    deviceId?: string;
    groupId?: string;
    initialSelectedIds?: string[];
    isUpdating?: boolean;
    maxCount?: number;
}

export const SelectLoadGroupsModal = ({
    isVisible,
    onCancel,
    onConfirm,
    initialSelectedIds = [],
    isUpdating = false,
    maxCount,
}: SelectLoadGroupsModalProps): JSX.Element => {
    const { t } = useTranslation();
    const [searchKey, setSearchKey] = useState('');
    const [, debouncedValue] = useDebounce(undefined, 600, searchKey);
    const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);
    const [selectedRecordsMap, setSelectedRecordsMap] = useState<Record<string, any>>({});
    useNewCustomTranslations('WIDGETS');
    const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
    const {
        page,
        listRecords,
        isLoading: isLoadGroupsLoading,
        isFetching: isLoadGroupsFetching,
        reset,
    } = useLoadGroupsScrollableList({
        containerRef: scrollContainerRef,
        forceListWithoutDevice: true,
        globalSearch: debouncedValue,
        pageSize: 10,
        sort: { key: 'name', sortType: 'ASC' },
        enabled: isVisible,
    });

    useEffect(() => {
        if (isVisible) {
            setSearchKey('');
            setSelectedIds(initialSelectedIds);
            setSelectedRecordsMap({});
        }
    }, [isVisible, initialSelectedIds]);

    const { data: selectedGroupsResponse }: any = useGetLoadGroupsListAllQuery(
        {
            ids: selectedIds,
            page: 0,
            size: selectedIds.length || 1,
            sort: { key: 'name', sortType: 'ASC' },
        },
        {
            skip: !isVisible || !selectedIds.length,
            refetchOnMountOrArgChange: true,
        }
    );

    const records = useMemo(() => listRecords ?? [], [listRecords]);
    const selectedRecords = useMemo(
        () => selectedGroupsResponse?.data?.records ?? [],
        [selectedGroupsResponse?.data?.records]
    );

    useEffect(() => {
        if (!records.length) return;
        setSelectedRecordsMap((prev) => {
            const next = { ...prev };
            records.forEach((item: any) => {
                if (item?.id) {
                    next[item.id] = item;
                }
            });
            return next;
        });
    }, [records]);

    useEffect(() => {
        if (!selectedRecords.length) return;
        setSelectedRecordsMap((prev) => {
            const next = { ...prev };
            selectedRecords.forEach((item: any) => {
                if (item?.id) {
                    next[item.id] = item;
                }
            });
            return next;
        });
    }, [selectedRecords]);

    const selectedData = useMemo(
        () => selectedIds.map((id) => selectedRecordsMap[id]).filter((item) => Boolean(item)),
        [selectedIds, selectedRecordsMap]
    );

    const isLoading = isLoadGroupsLoading || isLoadGroupsFetching;

    const handleToggleSelection = (item: any): void => {
        const id = item?.id;
        if (!id) return;

        setSelectedRecordsMap((prev) => ({ ...prev, [id]: item }));
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
    };

    const handleRemoveSelected = (id: string): void => {
        setSelectedIds((prev) => prev.filter((value) => value !== id));
    };

    const handleSave = (): void => {
        const orderedSelectedGroups = selectedIds.map((id) => selectedRecordsMap[id]).filter((item) => Boolean(item));
        onConfirm(orderedSelectedGroups);
    };

    const handleDragEnd = (result: DropResult): void => {
        if (!result.destination) return;

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex) return;

        setSelectedIds((prev) => {
            const next = [...prev];
            const [moved] = next.splice(sourceIndex, 1);
            next.splice(destinationIndex, 0, moved);
            return next;
        });
    };

    const getSubtitle = (item: any): JSX.Element =>
        item?.isMultipleDevice ? (
            <Stack direction="row" alignItems="center" spacing={1}>
                <Link sx={{ color: '#727E84', fontSize: 16 }} />
                <Typography fontSize={12} fontWeight={400} color="#333D43">
                    Multiple Devices
                </Typography>
            </Stack>
        ) : (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ maxWidth: '350px' }}>
                <Ups sx={{ color: '#727E84', fontSize: 16 }} />
                <GroupPath pathName={item?.groupPath ?? []} deviceName={item?.deviceName} />
            </Stack>
        );

    return (
        <CustomModal
            isOpen={isVisible}
            type="large"
            header={t('WIDGETS:LOADS_GROUPS.ADD_LOAD_GROUPS')}
            actions={[
                {
                    key: 'cancel',
                    label: 'Cancel',
                    variant: 'outlined',
                    handleClick: (): void => {
                        reset();
                        onCancel();
                    },
                },
                {
                    key: 'save',
                    label: 'Save',
                    variant: 'contained',
                    disabled: !selectedIds?.length || isUpdating,
                    handleClick: handleSave,
                },
            ]}
            hidePadding
            actionsDivider
        >
            <Grid container p={2} justifyContent={'space-between'}>
                <Grid px={2} py={3} className={`bg-content-wrapper w-49`}>
                    <Stack>
                        <Typography variant="body1" fontSize={'16px'} fontWeight={'600'} pb={2}>
                            <CustomTransComponent translationKey={'COMMON:SELECT_LOAD_GROUPS'} />
                        </Typography>
                        <InputWithCustomPlaceholder
                            placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                            inputProps={{ 'aria-label': 'search' }}
                            className="w-100"
                            value={searchKey}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setSearchKey(e.target.value)}
                        />
                        <Box
                            ref={scrollContainerRef}
                            sx={{
                                backgroundColor: '#ffff',
                                maxHeight: 338,
                                overflowY: 'auto',
                            }}
                            mt={3}
                        >
                            <List disablePadding>
                                {isLoading && page === 0 ? (
                                    <ListSkeleton total={10} />
                                ) : (
                                    records?.map((item: any) => (
                                        <InfoListItem
                                            sx={{
                                                height: '6.25rem',
                                            }}
                                            title={''}
                                            key={item?.id}
                                            divider="full"
                                            hidePadding={true}
                                            leftComponent={
                                                <Stack className="white-space-pre">
                                                    <Typography
                                                        sx={{
                                                            fontSize: '16px',
                                                            fontWeight: '600',
                                                            color: '#424E54',
                                                            maxWidth: '350px',
                                                        }}
                                                    >
                                                        {item?.name ?? ''}
                                                    </Typography>

                                                    {getSubtitle(item)}
                                                    {Boolean(item?.description) && (
                                                        <Typography
                                                            fontSize={12}
                                                            fontWeight={400}
                                                            color="#333D43"
                                                            sx={{
                                                                marginY: '2px',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                maxWidth: '350px',
                                                            }}
                                                        >
                                                            {item?.description}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            }
                                            rightComponent={
                                                <Checkbox
                                                    edge="end"
                                                    onChange={(): void => handleToggleSelection(item)}
                                                    checked={selectedIds.includes(item?.id)}
                                                />
                                            }
                                        />
                                    ))
                                )}
                                {isLoading && page > 0 && <ListSkeleton total={2} />}
                            </List>
                        </Box>
                    </Stack>
                </Grid>
                <Grid py={3} px={2} className={`bg-content-wrapper w-49`}>
                    <Stack>
                        <Typography variant="subtitle1" fontWeight={600}>
                            <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.SELECTED_LOAD_GROUPS'} />
                        </Typography>
                        <Typography variant="body1" fontWeight={400} py={2}>
                            <CustomTransComponent
                                translationKey={'WIDGETS:COMMON.REARRANGE_CHANNELS_DESCRIPTION'}
                                replace={{
                                    channelType: t('WIDGETS:BALANCING.TARGET_LOAD_GROUPS'),
                                    count: `(${maxCount ?? 6})`,
                                }}
                            />{' '}
                        </Typography>
                        <Divider />
                        <Box>
                            <Typography
                                variant="overline"
                                fontWeight={600}
                                fontSize={'12px'}
                                letterSpacing={2}
                                pl={2}
                                py={'12px'}
                            >
                                <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.SELECTED_LOAD_GROUPS'} />
                            </Typography>
                            <DragDropContext onDragEnd={handleDragEnd}>
                                <Stack sx={{ backgroundColor: '#ffff' }}>
                                    <Droppable droppableId="selected-load-groups">
                                        {(provided): JSX.Element => (
                                            <List
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                disablePadding
                                                sx={{
                                                    maxHeight: 278,
                                                    minHeight: 64,
                                                    overflowY: 'auto',
                                                    backgroundColor: '#ffff',
                                                }}
                                            >
                                                {selectedData.map((item: any, index: number) => (
                                                    <Draggable key={item?.id} draggableId={item?.id} index={index}>
                                                        {(draggableProvided): JSX.Element => (
                                                            <List
                                                                ref={draggableProvided.innerRef}
                                                                {...draggableProvided.draggableProps}
                                                                sx={{
                                                                    borderBottom: '1px solid #E0E5E7',
                                                                    px: 1,
                                                                    py: 1.5,
                                                                }}
                                                            >
                                                                <Stack
                                                                    direction="row"
                                                                    alignItems="flex-start"
                                                                    justifyContent={'space-between'}
                                                                    spacing={1}
                                                                >
                                                                    <Stack flexDirection={'row'} alignItems={'center'}>
                                                                        <Box
                                                                            {...draggableProvided.dragHandleProps}
                                                                            sx={{ pt: '2px', pr: '16px' }}
                                                                        >
                                                                            <DragHandle sx={{ color: '#727E84' }} />
                                                                        </Box>
                                                                        <Stack
                                                                            spacing={0.5}
                                                                            sx={{ flex: 1, maxWidth: '350px' }}
                                                                        >
                                                                            <Typography variant="body1">
                                                                                {item?.name ?? ''}
                                                                            </Typography>
                                                                            {getSubtitle(item)}
                                                                            {Boolean(item?.description) && (
                                                                                <Typography
                                                                                    fontSize={12}
                                                                                    fontWeight={400}
                                                                                    color="#333D43"
                                                                                    sx={{
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis',
                                                                                        whiteSpace: 'nowrap',
                                                                                        maxWidth: '100%',
                                                                                    }}
                                                                                >
                                                                                    {item?.description}
                                                                                </Typography>
                                                                            )}
                                                                        </Stack>
                                                                    </Stack>
                                                                    <IconButton
                                                                        onClick={(): void =>
                                                                            handleRemoveSelected(item?.id ?? '')
                                                                        }
                                                                    >
                                                                        <Close sx={{ color: '#727E84' }} />
                                                                    </IconButton>
                                                                </Stack>
                                                            </List>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </List>
                                        )}
                                    </Droppable>
                                </Stack>
                            </DragDropContext>
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
        </CustomModal>
    );
};
